import { Component } from '@angular/core';
import { LogService, LogDetails } from 'src/app/services/log.service';

interface Filter {
  type: 'ip' | 'user' | 'url';
  value: string;
}

@Component({
  selector: 'app-log-search',
  templateUrl: './log-search.component.html',
  styleUrl: './log-search.component.scss',
})
export class LogSearchComponent {
  readonly minSearchLength = 3;

  searchQuery: string = '';
  startDate: string = '';
  endDate: string = '';
  loading: boolean = false;
  searchResults: LogDetails[] = [];
  filteredResults: LogDetails[] = [];
  paginatedResults: LogDetails[] = [];
  error: string = '';
  filters: Filter[] = [];
  hoveredItem: { type: 'ip' | 'user' | 'url'; value: string } | null = null;

  // Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;

  Math = Math;
  // Tracks whether logs are expanded per request_id
  expandedLogs: { [requestId: string]: boolean } = {};

  constructor(private logService: LogService) {
    this.resetDateRangeToDefaults();
  }

  get canSearch(): boolean {
    const q = this.searchQuery.trim();
    return (
      q.length >= this.minSearchLength &&
      !!this.startDate &&
      !!this.endDate &&
      !this.loading
    );
  }

  onSearch() {
    const q = this.searchQuery.trim();
    if (!q) {
      this.error = 'Search message is required.';
      return;
    }
    if (q.length < this.minSearchLength) {
      this.error = `Search message must be at least ${this.minSearchLength} characters.`;
      return;
    }
    if (!this.startDate) {
      this.error = 'Start date is required.';
      return;
    }
    if (!this.endDate) {
      this.error = 'End date is required.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.searchResults = [];
    this.filteredResults = [];
    this.paginatedResults = [];
    this.filters = [];
    this.currentPage = 1;

    this.logService
      .searchLogs(
        q,
        this.localDateTimeToUtc(this.startDate),
        this.localDateTimeToUtc(this.endDate)
      )
      .subscribe({
        next: (results) => {
          this.loading = false;
          this.searchResults = results;
          this.applyFilters();
          if (results.length === 0) {
            this.error = 'No logs found matching your search criteria';
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error searching logs:', error);
          this.error =
            'An error occurred while searching logs. Please try again.';
        },
      });
  }

  onClear() {
    this.searchQuery = '';
    this.resetDateRangeToDefaults();
    this.searchResults = [];
    this.filteredResults = [];
    this.paginatedResults = [];
    this.error = '';
    this.filters = [];
    this.currentPage = 1;
    this.totalPages = 0;
  }

  addFilter(type: 'ip' | 'user' | 'url', value: string) {
    if (!value || value === 'N/A') return;

    const filterExists = this.filters.some(
      (f) => f.type === type && f.value === value
    );
    if (!filterExists) {
      this.filters.push({ type, value });
      this.applyFilters();
    }
    this.hoveredItem = null;
  }

  removeFilter(index: number) {
    this.filters.splice(index, 1);
    this.applyFilters();
  }

  clearFilters() {
    this.filters = [];
    this.applyFilters();
  }

  applyFilters() {
    if (this.filters.length === 0) {
      this.filteredResults = [...this.searchResults];
    } else {
      this.filteredResults = this.searchResults.filter((log) => {
        return this.filters.every((filter) => {
          switch (filter.type) {
            case 'ip':
              return log.client_ip === filter.value;
            case 'user':
              return log.email === filter.value;
            case 'url':
              return log.url === filter.value;
            default:
              return true;
          }
        });
      });
    }

    // Reset to first page when filters change
    this.currentPage = 1;
    this.updatePagination();
  }

  updatePagination() {
    this.totalPages = Math.ceil(
      this.filteredResults.length / this.itemsPerPage
    );

    // Ensure current page is valid
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedResults = this.filteredResults.slice(startIndex, endIndex);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxPagesToShow / 2)
    );
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  setHoveredItem(type: 'ip' | 'user' | 'url', value: string) {
    if (value && value !== 'N/A') {
      this.hoveredItem = { type, value };
    }
  }

  clearHoveredItem() {
    this.hoveredItem = null;
  }

  isHovered(type: 'ip' | 'user' | 'url', value: string): boolean {
    return this.hoveredItem?.type === type && this.hoveredItem?.value === value;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    if (date.getTime() < 1000) return 'N/A';
    return date.toLocaleString();
  }

  highlightSearchText(text: string, searchQuery: string): string {
    if (!text || !searchQuery) {
      return this.escapeHtml(text);
    }

    const escapedText = this.escapeHtml(text);
    const escapedQuery = this.escapeHtml(searchQuery);

    // Create a case-insensitive regex to find all matches
    const regex = new RegExp(`(${this.escapeRegex(escapedQuery)})`, 'gi');

    return escapedText.replace(
      regex,
      '<mark class="bg-yellow-300 dark:bg-yellow-600">$1</mark>'
    );
  }

  private escapeHtml(text: string): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private resetDateRangeToDefaults(): void {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    this.endDate = this.toDateTimeLocalValue(now);
    this.startDate = this.toDateTimeLocalValue(weekAgo);
  }

  private toDateTimeLocalValue(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // datetime-local is local wall time. Server query params stay UTC.
  private localDateTimeToUtc(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(
      value
    );
    if (!match) {
      return value;
    }
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = match[6] ? Number(match[6]) : 0;
    return new Date(year, month, day, hour, minute, second).toISOString();
  }

  toggleLogsExpansion(logDetail: LogDetails): void {
    const id = logDetail.request_id;
    this.expandedLogs[id] = !this.expandedLogs[id];
  }

  areLogsExpanded(logDetail: LogDetails): boolean {
    return !!this.expandedLogs[logDetail.request_id];
  }

  getVisibleLogs(logDetail: LogDetails) {
    if (this.areLogsExpanded(logDetail) || !logDetail.logs) {
      return logDetail.logs;
    }

    return logDetail.logs.length > 10
      ? logDetail.logs.slice(0, 10)
      : logDetail.logs;
  }
}
